import { formatTaskDate } from './formatters'
import { isComposeMode } from './officeHelpers'
import type { ClaimTask } from '../types/task'

/* global Office */

// Opens Outlook's own New Event form pre-filled with the task, so the user can save a
// personal reminder without leaving the pane (MSD-684). Nothing is written back to MC1
// — the add-in only hands Outlook a draft; the user saves it (or doesn't).
//
// Platform limitations, straight from the Office.js contract for
// displayNewAppointmentForm(Async):
//   • Read mode only — the API is unavailable while composing a message.
//   • Not implemented in Outlook on Android or iOS.
//   • The async form (callback, and therefore success/failure feedback) needs Mailbox
//     1.9; the manifest only asks for 1.5, so older desktop builds fall back to the
//     synchronous form, which reports nothing.
// Anywhere it is unavailable the button is disabled and says why, rather than opening
// nothing.

export interface EventReminderAvailability {
  supported: boolean
  // Shown under the disabled button. Undefined when supported.
  reason?: string
}

export const EVENT_REMINDER_MOBILE_REASON = 'Calendar reminders are not available in Outlook on mobile.'
export const EVENT_REMINDER_COMPOSE_REASON = 'Open the task from a received email to add a calendar reminder.'
export const EVENT_REMINDER_HOST_REASON = 'Calendar reminders are not available in this version of Outlook.'

// The reminder opens at 09:00 local on the target date and runs half an hour, so it
// lands at the top of that working day. The user can move it in the form before saving.
const REMINDER_START_HOUR = 9
const REMINDER_DURATION_MS = 30 * 60 * 1000

function isMobilePlatform(): boolean {
  const platform = Office?.context?.platform
  if (platform == null) return false
  return platform === Office.PlatformType?.iOS || platform === Office.PlatformType?.Android
}

export function getEventReminderAvailability(): EventReminderAvailability {
  try {
    const mailbox = Office?.context?.mailbox
    if (!mailbox) return { supported: false, reason: EVENT_REMINDER_HOST_REASON }
    if (isMobilePlatform()) return { supported: false, reason: EVENT_REMINDER_MOBILE_REASON }
    if (isComposeMode()) return { supported: false, reason: EVENT_REMINDER_COMPOSE_REASON }
    // Mobile is the documented gap, but a host that simply doesn't expose the API is
    // caught here too rather than throwing on click.
    const hasApi =
      typeof mailbox.displayNewAppointmentFormAsync === 'function' ||
      typeof mailbox.displayNewAppointmentForm === 'function'
    if (!hasApi) return { supported: false, reason: EVENT_REMINDER_HOST_REASON }
    return { supported: true }
  } catch {
    return { supported: false, reason: EVENT_REMINDER_HOST_REASON }
  }
}

/**
 * Event subject: "First Contact - GB2500123 - Target Date: Mar 01 2026".
 * A task with no usable target date drops that segment rather than showing a blank one.
 */
export function buildEventReminderSubject(task: ClaimTask, claimNumber: string): string {
  const targetDate = formatTaskDate(task.targetDate)
  const segments = [task.type?.trim(), claimNumber?.trim(), targetDate && `Target Date: ${targetDate}`]
  return segments.filter(Boolean).join(' - ')
}

/**
 * The 09:00–09:30 slot on the target date, in the user's own timezone.
 *
 * MC1 sends timezone-less timestamps ("2026-03-01T00:00:00"), so the calendar date is
 * read off the string rather than through `new Date()` — parsing as UTC and rendering
 * as local shifts the event a day backwards east of Greenwich. Returns null for a
 * missing date or MC1's zero-date sentinel, which leaves the form on Outlook's default
 * slot instead of proposing the year 1900.
 */
export function getEventReminderTimes(targetDate: string | null | undefined): { start: Date; end: Date } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(targetDate ?? '')
  if (!match) return null

  const [, year, month, day] = match
  const start = new Date(Number(year), Number(month) - 1, Number(day), REMINDER_START_HOUR, 0, 0, 0)
  if (Number.isNaN(start.getTime()) || Number(year) <= 1900) return null

  return { start, end: new Date(start.getTime() + REMINDER_DURATION_MS) }
}

/**
 * Hands Outlook the draft event. Resolves once the form has been opened — that is all
 * Office.js reports; whether the user then saves it is not observable from here.
 */
export function displayEventReminder(parameters: Office.AppointmentForm): Promise<void> {
  const mailbox = Office?.context?.mailbox
  if (!mailbox) return Promise.reject(new Error(EVENT_REMINDER_HOST_REASON))

  if (typeof mailbox.displayNewAppointmentFormAsync === 'function') {
    return new Promise((resolve, reject) => {
      mailbox.displayNewAppointmentFormAsync(parameters, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve()
          return
        }
        reject(new Error(result.error?.message ?? 'Outlook could not open the event form.'))
      })
    })
  }

  // Pre-1.9 hosts: no callback, so a call that doesn't throw is the only success signal
  // there is.
  try {
    mailbox.displayNewAppointmentForm(parameters)
    return Promise.resolve()
  } catch (err: unknown) {
    return Promise.reject(err instanceof Error ? err : new Error(EVENT_REMINDER_HOST_REASON))
  }
}
